import type { MonsterDTO } from '@shared/protocol/messages';
import type { CombatStats } from '../combat/engine';
import { CHAPTERS, PRESET_MONSTERS, type PresetMonster } from '../data/presets';
import { newId, type Monster } from './model';
import { isBossFloor, towerFloorOf, towerMonster } from './tower';
import { boardMonsterFromId } from './board';
import { aiLevelFor } from '../combat/ai';

export function presetToMonster(p: PresetMonster): Monster {
  return {
    id: `preset:${p.id}`,
    name: p.name,
    hp: p.hp,
    atk: p.atk,
    def: p.def,
    luck: p.luck,
    skill: p.skill,
    personality: p.personality,
    reward: { ...p.reward },
    mode: 'public',
    tier: p.tier,
    drawing: null,
    preset: p.id,
    source: 'preset',
    wins: 0,
    losses: 0,
    createdAt: 0,
  };
}

export const PRESET_MONSTER_LIST: Monster[] = PRESET_MONSTERS.map(presetToMonster);

export function findMonster(own: readonly Monster[], id: string): Monster | undefined {
  const floor = towerFloorOf(id);
  if (floor !== null) return towerMonster(floor);
  if (id.startsWith('board:')) return boardMonsterFromId(id) ?? undefined;
  return PRESET_MONSTER_LIST.find((m) => m.id === id) ?? own.find((m) => m.id === id);
}

/** Built-in monsters (story, tower, board) have English names that are translated; player names never are. */
export function hasBuiltInName(m: Pick<Monster, 'source'>): boolean {
  return m.source === 'preset' || m.source === 'tower' || m.source === 'board';
}

/** How sharply a monster plays: story monsters by their level, tower monsters by floor, everything else classic. */
export function aiLevelOf(m: Monster): number {
  if (m.source === 'tower') return aiLevelFor(towerFloorOf(m.id) ?? 0);
  if (m.source !== 'preset') return 0;
  return aiLevelFor(PRESET_MONSTERS.find((p) => p.id === m.preset)?.level ?? 0);
}

/** Each chapter's last stage. */
const CHAPTER_BOSSES = new Set(CHAPTERS.map((c) => c.stages[c.stages.length - 1]));

/** Chapter bosses, tower boss floors and the weekly boss fight like bosses (combat/twists.ts). */
export function isBossMonster(m: Monster): boolean {
  if (m.source === 'boss') return true;
  if (m.source === 'tower') {
    const floor = towerFloorOf(m.id);
    return floor !== null && isBossFloor(floor);
  }
  return m.source === 'preset' && !!m.preset && CHAPTER_BOSSES.has(m.preset);
}

export function monsterCombat(m: Monster): CombatStats {
  return { name: m.name, maxHp: m.hp, atk: m.atk, def: m.def, luck: m.luck, skill: m.skill };
}

export function monsterToDTO(m: Monster): MonsterDTO {
  const dto: MonsterDTO = {
    name: m.name,
    hp: m.hp,
    atk: m.atk,
    def: m.def,
    luck: m.luck,
    skill: m.skill,
    personality: m.personality,
    reward: { ...m.reward },
    mode: m.mode,
    tier: m.tier,
    drawing: m.drawing,
  };
  if (m.preset) dto.preset = m.preset;
  return dto;
}

export function monsterFromDTO(dto: MonsterDTO, source: Monster['source'], author?: string, shareCode?: string): Monster {
  const m: Monster = {
    id: newId('m'),
    name: dto.name,
    hp: dto.hp,
    atk: dto.atk,
    def: dto.def,
    luck: dto.luck,
    skill: dto.skill,
    personality: dto.personality,
    reward: { ...dto.reward },
    mode: dto.mode,
    tier: dto.tier,
    drawing: dto.drawing,
    source,
    wins: 0,
    losses: 0,
    createdAt: Date.now(),
  };
  if (dto.preset) m.preset = dto.preset;
  if (author) m.author = author;
  if (shareCode) m.shareCode = shareCode;
  return m;
}

/** Monster art id in the atlas, or null when the monster has its own drawing. */
export function monsterArt(m: Pick<Monster, 'drawing' | 'preset'>): string | null {
  if (m.drawing && m.drawing.strokes.length > 0) return null;
  return `monster:${m.preset ?? 'gum_blob'}`;
}
